import * as S from "./styles.ts";
import * as React from "react";
import {
  CSSProperties,
  forwardRef,
  HTMLAttributes,
  ReactElement,
  RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import TextareaAutosize from "react-textarea-autosize";
import { IPopupProps } from "@template/components/Popup";
import { TDropdownOption } from "@template/components/Select/Dropdown";
import ColorPicker from "@template/components/Input/ColorPicker";
import LoadingSpinner from "@template/components/LoadingSpinner";
import { Paperclip } from "@template/icons";
import { useTheme } from "styled-components";
import { RenderMarkdown } from "@template/components";

export const styles = S;

export interface IInputProps extends HTMLAttributes<HTMLInputElement> {
  startContent?: any;
  endContent?: any;
  label?: any;
  hint?: string | ReactElement;
  disabled?: boolean;
  error?: string | boolean;
  prefix?: any;
  suffix?: any;
  placeholder?: string;
  value?: string | number | File | boolean;
  onChange?: (val: any, e?: any) => void;
  type?:
    | "text"
    | "password"
    | "number"
    | "email"
    | "textarea"
    | "select"
    | "tel"
    | "color"
    | "checkbox"
    | "file";
  ref?: RefObject<HTMLInputElement>;
  fullWidth?: boolean;
  name?: string;
  autoComplete?: string;
  required?: boolean;
  hiddenRequired?: boolean;
  validation?: {
    regex?: RegExp;
    custom?: (val: string | number, e?: any) => boolean;
    error: string;
  };
  regexError?: string;
  inputProps?: any;
  loading?: boolean;
  readOnly?: boolean;
  options?: TDropdownOption[];
  selected?: string | number;
  search?: boolean;
  id?: string;
  variant?: "light" | "solid" | "bordered" | "minimal";
  labelPosition?: "inside" | "outside" | "inside-top";
  ariaLabel?: string;
  autoHeight?: boolean;
  max?: number;
  popupProps?: Partial<IPopupProps>;
  style?: CSSProperties;
  render?: any;
}

export const UniversalMarkdown = ({ children: value }: any) => {
  if (typeof value === "string")
    return (
      <RenderMarkdown>
        {value.split("\n").map((line, index) => ({
          type: "paragraph",
          children: [
            {
              text: line,
            },
          ],
        }))}
      </RenderMarkdown>
    );

  return <RenderMarkdown>{value}</RenderMarkdown>;
};

export const handleContent = (
  item: IInputProps["startContent"] | IInputProps["endContent"]
) => {
  if (!item) return null;

  if (typeof item === "string") {
    return <S.InputContent>{item}</S.InputContent>;
  }

  return item;
};

const checkRegex = (value: string, regex: RegExp) => {
  return regex.test(value);
};

const Input = forwardRef(
  (
    {
      startContent,
      endContent,
      label,
      hint,
      disabled,
      error,
      prefix,
      suffix,
      placeholder,
      value,
      onChange,
      type,
      fullWidth,
      name,
      autoComplete = "one-time-code",
      ariaLabel,
      required,
      hiddenRequired,
      validation,
      inputProps,
      loading,
      readOnly,
      id,
      variant,
      labelPosition = "outside",
      autoHeight,
      render,
      ...props
    }: IInputProps,
    ref: any
  ) => {
    const theme = useTheme();

    const [defaultValue, setDefaultValue] = useState<string | number>("");
    const [regError, setRegError] = useState("");

    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // const usedValue = value || defaultValue;
    const handleUsedValue = () => {
      switch (true) {
        case type === "file":
          if (value) {
            return defaultValue || value;
          }
          break;
        case !value && !defaultValue:
          return "";
      }

      return value || defaultValue;
    };

    const usedValue = handleUsedValue();

    const handleChange = (val: string | number, e: any) => {
      switch (type) {
        case "number":
          if (props.max && Number(val) > props.max) {
            return props.max;
          }
          break;
        case "file":
          const files = e.target.files;
          if (files && files.length) {
            return files;
          }
          break;
      }
      return val;
    };
    const usedOnChange = onChange || ((val) => setDefaultValue(val));

    const usedInputRef = ref || inputRef;

    const textArea = type === "textarea";

    useEffect(() => {
      if (usedInputRef.current && wrapperRef.current) {
        const handleClick = () => {
          if (usedInputRef.current) {
            usedInputRef.current.focus();
          }
        };
        wrapperRef.current.addEventListener("click", handleClick);

        if (validation) {
          const handleValidation = (e: any) => {
            const val = e.target.value;
            if (!val) return;
            if (validation) {
              let isValid = true;
              if (validation.regex) {
                isValid = checkRegex(val, validation.regex);
              }
              if (validation.custom) {
                isValid = validation.custom(val, e);
              }
              if (!isValid) {
                setRegError(validation.error);
              }
            }
          };

          usedInputRef.current.addEventListener("focus", () => setRegError(""));
          usedInputRef.current.addEventListener("blur", handleValidation);

          return () => {
            usedInputRef.current?.removeEventListener(
              "input",
              handleValidation
            );
            wrapperRef.current?.removeEventListener("click", handleClick);

            usedInputRef.current?.removeEventListener("focus", () =>
              setRegError("")
            );
            usedInputRef.current?.removeEventListener("blur", handleValidation);
          };
        }

        return () => {
          wrapperRef.current?.removeEventListener("click", handleClick);
        };
      }
    }, [usedInputRef, wrapperRef]);

    return (
      <S.InputWrapper
        $disabled={disabled}
        $error={
          error === true
            ? true
            : typeof error === "string"
              ? error?.length > 0
              : regError.length > 0
        }
        ref={wrapperRef}
        $fullWidth={fullWidth}
        $variant={variant}
        {...props}
      >
        {label && labelPosition === "outside" && (
          <S.InputLabel>
            {label}
            {required && !hiddenRequired && <S.RequiredStar>*</S.RequiredStar>}
          </S.InputLabel>
        )}
        <S.InputContainer {...props}>
          {type === "color" && (
            <ColorPicker
              color={usedValue as string}
              onChange={usedOnChange}
            />
          )}
          {label && labelPosition === "inside-top" && (
            <S.InputLabel $labelPosition={labelPosition}>
              {label}
              {required && !hiddenRequired && (
                <S.RequiredStar>*</S.RequiredStar>
              )}
            </S.InputLabel>
          )}
          <S.Inline>
            {label && labelPosition === "inside" && (
              <S.InputLabel $labelPosition={labelPosition}>
                {label}
                {required && !hiddenRequired && (
                  <S.RequiredStar>*</S.RequiredStar>
                )}
              </S.InputLabel>
            )}
            {prefix && <S.Prefix>{prefix}</S.Prefix>}
            {!prefix && type === "file" && (
              <S.Prefix>
                <Paperclip
                  color={theme?.colors.default[600]}
                  style={{ minHeight: 24 }}
                />
              </S.Prefix>
            )}
            <S.InputContentContainer>
              {handleContent(startContent)}
              {textArea ? (
                <S.TextArea
                  as={autoHeight ? TextareaAutosize : "textarea"}
                  value={usedValue}
                  onChange={(e: any) =>
                    usedOnChange(handleChange(e.target.value, e), e)
                  }
                  placeholder={placeholder}
                  disabled={disabled}
                  ref={usedInputRef}
                  name={name || label || placeholder}
                  autoComplete={(autoComplete && name) || undefined}
                  readOnly={readOnly}
                  aria-label={ariaLabel}
                  {...(inputProps as any)}
                />
              ) : (
                <S.Input
                  value={usedValue}
                  size={1}
                  onChange={(e) => {
                    if (type === "file") {
                      usedOnChange(handleChange(e.target.value, e), e);
                      setDefaultValue(e.target.value);
                    } else {
                      usedOnChange(handleChange(e.target.value, e), e);
                    }
                  }}
                  type={type === "color" ? "text" : type}
                  autoFocus={props.autoFocus}
                  placeholder={placeholder}
                  disabled={disabled}
                  ref={usedInputRef}
                  name={name || label || placeholder}
                  autoComplete={autoComplete || undefined}
                  readOnly={readOnly}
                  id={id}
                  aria-label={ariaLabel}
                  data-has-value={!!usedValue}
                  data-type={type}
                  {...(inputProps as any)}
                />
              )}
              {loading &&
                handleContent(
                  <LoadingSpinner
                    height={14}
                    color="default"
                  />
                )}
              {type === "color" &&
                handleContent(<S.ColorCircle $color={usedValue as string} />)}
              {handleContent(endContent)}
            </S.InputContentContainer>
            {suffix && <S.Suffix>{suffix}</S.Suffix>}
          </S.Inline>
        </S.InputContainer>
        {hint && typeof error !== "string" && !regError && (
          <S.Hint>
            <UniversalMarkdown>{hint}</UniversalMarkdown>
          </S.Hint>
        )}
        {(typeof error === "string" || regError) && error && (
          <S.Error>{error || regError}</S.Error>
        )}
      </S.InputWrapper>
    );
  }
);

export default Input;
